import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "tab",
        "pane",
        "apiTabs",
        "apiKey",
        "delayOptions",
        "parentId",
        "kycLevel",
        "i18n",
        "integrationType",
        "firstNameCheckbox",
        "firstNameFields",
        "firstNameLabel",
        "firstNameVisibleCheckbox",
        "firstNameValue",
        "lastNameCheckbox",
        "lastNameFields",
        "lastNameLabel",
        "lastNameVisibleCheckbox",
        "lastNameValue",
        "emailCheckbox",
        "emailFields",
        "emailLabel",
        "emailVisibleCheckbox",
        "emailValue",
        "phoneCheckbox",
        "phoneFields",
        "phoneLabel",
        "phoneVisibleCheckbox",
        "phoneValue",
        "identifierCheckbox",
        "identifierFields",
        "identifierPlaceholder",
        "identifierVisibleCheckbox",
        "identifierValue",
        "verificationFlow",
        "previewForm",
        "previewFirstName",
        "previewLastName",
        "previewIdentifier",
        "previewIdentifierLabel",
        "previewEmail",
        "previewPhone",
        "codeContent",
        "codeBlock",
        "copyButton",
        "previewIframe",
        "previewBtn",
        "previewCopyBtn",
        "toggleSizeBtn",
        "toggleSizeIcon",
        "fullscreenContainer",
        "fullscreenIframe",
        "previewFrame",
        "hostedCheckbox",
        "hostedFields",
        "pageTitle",
        "pageTitleField",
        "pageDescription",
        "pageDescriptionField",
        "namesCheckbox",
        "preview",
        "previewTitle",
        "previewDescription",
        "previewNames",
        "previewTerms",
        "desktopView",
        "mobileView"
    ]

    // Keep track of elements we need to clean up
    styleElement = null
    bynnScriptElement = null

    connect() {
        try {
            // Generate initial code
            if (this.hasCodeContentTarget) {
                this.updateCodePreview()
            }
            
            // Initialize the live preview with a slight delay to avoid conflicts
            if (this.hasPreviewIframeTarget) {
                setTimeout(() => this.renderLivePreview(), 100)
            }
            
            // Initialize any tab activation if tabs exist
            if (this.hasTabTarget && this.tabTargets.length > 0) {
                this.activate({ preventDefault: () => {}, currentTarget: this.tabTargets[0] })
            }
            
            // Set up a mutation observer to watch for theme changes
            this.setupThemeObserver();
        } catch (error) {
            console.error("Error initializing integration controller:", error)
        }
    }
    
    // Set up an observer to watch for theme changes
    setupThemeObserver() {
        // Create a MutationObserver to watch for changes to the html element's data-bs-theme attribute
        this.themeObserver = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.attributeName === 'data-bs-theme') {
                    // Check if iframe is maximized
                    if (this.hasPreviewIframeTarget && this.previewIframeTarget.classList.contains('maximized')) {
                        // Update the background color based on the new theme
                        const isDarkMode = document.querySelector('html').getAttribute('data-bs-theme') === 'dark';
                        this.previewIframeTarget.style.backgroundColor = isDarkMode ? '#1a1a1a' : '#ffffff';
                        
                        // Ensure z-index remains at the extreme level even after theme changes
                        this.previewIframeTarget.style.zIndex = '500000';
                        
                        // Also ensure the preview window container keeps the same z-index
                        const previewWindow = this.previewIframeTarget.closest('.preview-window');
                        if (previewWindow) {
                            previewWindow.style.zIndex = '500000';
                        }
                    }
                }
            });
        });
        
        // Start observing the html element
        const htmlElement = document.querySelector('html');
        if (htmlElement) {
            this.themeObserver.observe(htmlElement, { attributes: true });
        }
    }
    
    // This is a placeholder that will be replaced by the actual disconnect method at the end of the file
    
    // Toggle hosted solution fields visibility
    toggleHosted() {
        if (this.hasHostedCheckboxTarget && this.hasHostedFieldsTarget) {
            this.hostedFieldsTarget.style.display = this.hostedCheckboxTarget.checked ? 'block' : 'none'
            // No direct iframe update - the form save handler will update it
        }
    }
    
    // Toggle names field visibility
    toggleNames() {
        if (this.hasNamesCheckboxTarget && this.hasPreviewNamesTarget) {
            this.previewNamesTarget.style.display = this.namesCheckboxTarget.checked ? 'block' : 'none'
            // No direct iframe update - the form save handler will update it
        }
    }
    
    // Toggle identifier field visibility
    toggleIdentifier() {
        if (this.hasIdentifierCheckboxTarget && this.hasPreviewIdentifierTarget) {
            this.previewIdentifierTarget.style.display = this.identifierCheckboxTarget.checked ? 'block' : 'none'
            // No direct iframe update - the form save handler will update it
        }
    }
    
    // Toggle page title
    togglePageTitle() {
        if (this.hasPageTitleTarget && this.hasPageTitleFieldTarget && this.hasPreviewTitleTarget) {
            this.pageTitleFieldTarget.style.display = this.pageTitleTarget.checked ? 'block' : 'none'
            this.previewTitleTarget.style.display = this.pageTitleTarget.checked ? 'block' : 'none'
            // No direct iframe update - the form save handler will update it
        }
    }
    
    // Toggle page description
    togglePageDescription() {
        if (this.hasPageDescriptionTarget && this.hasPageDescriptionFieldTarget && this.hasPreviewDescriptionTarget) {
            this.pageDescriptionFieldTarget.style.display = this.pageDescriptionTarget.checked ? 'block' : 'none'
            this.previewDescriptionTarget.style.display = this.pageDescriptionTarget.checked ? 'block' : 'none'
            // No direct iframe update - the form save handler will update it
        }
    }
    
    // Update rate limit settings
    updateRateLimit() {
        // No direct iframe update - the form save handler will update it
    }
    
    // Update identifier label
    updateIdentifierLabel() {
        if (this.hasIdentifierPlaceholderTarget && this.hasPreviewIdentifierLabelTarget) {
            this.previewIdentifierLabelTarget.textContent = this.identifierPlaceholderTarget.value
            // No direct iframe update - the form save handler will update it
        }
    }
    
    // Update identifier placeholder
    updateIdentifierPlaceholder() {
        // No direct iframe update - the form save handler will update it
    }
    
    // Update page title
    updatePageTitle() {
        // No direct iframe update - the form save handler will update it
    }
    
    // Update page description
    updatePageDescription() {
        // No direct iframe update - the form save handler will update it
    }
    
    // Switch between desktop and mobile view
    switchView(event) {
        if (this.hasPreviewTarget && this.hasDesktopViewTarget && this.hasMobileViewTarget) {
            const view = event.currentTarget.dataset.view
            
            // Remove active class from both buttons
            this.desktopViewTarget.classList.remove('active')
            this.mobileViewTarget.classList.remove('active')
            
            // Add active class to clicked button
            event.currentTarget.classList.add('active')
            
            // Update preview window style
            if (view === 'desktop') {
                this.previewTarget.style.width = '100%'
            } else {
                this.previewTarget.style.width = '375px'
            }
            
            // This visual change doesn't require a server-side save, so we can update immediately
            if (this.hasPreviewFrameTarget) {
                this.resizeIframeToContent(this.previewFrameTarget)
            }
        }
    }
    
    // Update the hosted page preview iframe when form settings change
    updateHostedPreviewFrame() {
        if (this.hasPreviewFrameTarget) {
            // Use current URL and add random query parameter to force refresh
            const iframe = this.previewFrameTarget
            if (iframe.src) {
                const url = new URL(iframe.src)
                url.searchParams.set('t', Date.now())
                iframe.src = url.toString()
                
                // Setup iframe resize handler if it doesn't already exist
                if (!iframe.hasAttribute('data-resize-listener-setup')) {
                    iframe.setAttribute('data-resize-listener-setup', 'true')
                    
                    // Add load event listener to adjust height based on content
                    iframe.addEventListener('load', () => {
                        this.resizeIframeToContent(iframe)
                        
                        // Also set up a mutation observer on the iframe content if possible
                        try {
                            const iframeWindow = iframe.contentWindow
                            const iframeDocument = iframeWindow.document
                            
                            // Create a ResizeObserver for the iframe content
                            const resizeObserver = new ResizeObserver(() => {
                                this.resizeIframeToContent(iframe)
                            })
                            
                            // Observe the body element for size changes
                            resizeObserver.observe(iframeDocument.body)
                            
                            // Store the observer on the iframe for cleanup later
                            iframe._resizeObserver = resizeObserver
                        } catch (error) {
                            console.warn('Could not set up ResizeObserver for iframe content:', error)
                        }
                    })
                }
            }
        }
    }
    
    // Helper method to resize iframe based on content
    resizeIframeToContent(iframe) {
        try {
            // Get the document height from the iframe
            const height = iframe.contentWindow.document.body.scrollHeight
            
            // Add a small buffer for padding and borders
            iframe.style.height = (height + 30) + 'px'
        } catch (error) {
            console.warn('Error resizing iframe:', error)
        }
    }
    
    // Tab activation
    activate(event) {
        event.preventDefault()

        // Remove active class from all tabs and panes
        this.tabTargets.forEach(tab => {
            tab.classList.remove('active')
        })
        this.paneTargets.forEach(pane => {
            pane.classList.remove('active')
        })

        // Add active class to clicked tab
        const tab = event.currentTarget
        tab.classList.add('active')

        // Show corresponding pane
        const tabId = tab.getAttribute('data-tab')
        const pane = this.paneTargets.find(p => p.id === tabId)
        if (pane) {
            pane.classList.add('active')
        }
    }
    
    // API Keys tab activation
    showApiTab(event) {
        // Get all tabs in the API tabs section
        const tabs = event.currentTarget.closest('ul').querySelectorAll('a.nav-link')
        tabs.forEach(tab => {
            tab.classList.remove('active')
            tab.classList.remove('show')
            tab.setAttribute('aria-selected', 'false')
        })
        
        // Activate the clicked tab
        event.currentTarget.classList.add('active')
        event.currentTarget.classList.add('show')
        event.currentTarget.setAttribute('aria-selected', 'true')
        
        // Get all tab panes
        const tabId = event.currentTarget.getAttribute('data-tab')
        const tabPanes = document.querySelectorAll('.tab-pane')
        tabPanes.forEach(pane => {
            if (pane.id === tabId) {
                pane.classList.add('active')
                pane.classList.add('show')
            } else if (pane.id === 'production-keys' || pane.id === 'sandbox-keys') {
                pane.classList.remove('active')
                pane.classList.remove('show')
            }
        })
    }
    
    // Toggle between production and sandbox API keys
    toggleApiKeyEnvironment(event) {
        if (!this.hasApiKeyTarget) return
        
        const environment = event.currentTarget.value
        const apiKeyInput = this.apiKeyTarget
        
        if (environment === 'production') {
            // Use the production key from data attribute
            const productionKey = event.currentTarget.getAttribute('data-production-key')
            if (productionKey) {
                apiKeyInput.value = productionKey
                this.updateCodePreview()
            }
        } else if (environment === 'sandbox') {
            // Use the sandbox key from data attribute
            const sandboxKey = event.currentTarget.getAttribute('data-sandbox-key')
            if (sandboxKey) {
                apiKeyInput.value = sandboxKey
                this.updateCodePreview()
            }
        }
    }
    
    // Toggle delay options when timing selection changes
    toggleDelayOptions(event) {
        if (!this.hasDelayOptionsTarget) return
        
        const timing = event.currentTarget.value
        
        if (timing === 'delayed') {
            this.delayOptionsTarget.style.display = 'block'
        } else {
            this.delayOptionsTarget.style.display = 'none'
        }
    }

    // Update visual preview of the form
    updateVisualPreview() {
        // First Name visibility and label
        if (this.hasFirstNameCheckboxTarget && this.hasPreviewFirstNameTarget) {
            this.previewFirstNameTarget.style.display = this.firstNameCheckboxTarget.checked ? 'block' : 'none'
            
            // Update label if available
            if (this.hasFirstNameLabelTarget && this.previewFirstNameTarget.querySelector('label')) {
                this.previewFirstNameTarget.querySelector('label').textContent = this.firstNameLabelTarget.value
            }
        }
        
        // Last Name visibility and label
        if (this.hasLastNameCheckboxTarget && this.hasPreviewLastNameTarget) {
            this.previewLastNameTarget.style.display = this.lastNameCheckboxTarget.checked ? 'block' : 'none'
            
            // Update label if available
            if (this.hasLastNameLabelTarget && this.previewLastNameTarget.querySelector('label')) {
                this.previewLastNameTarget.querySelector('label').textContent = this.lastNameLabelTarget.value
            }
        }
        
        // Email visibility and label
        if (this.hasEmailCheckboxTarget && this.hasPreviewEmailTarget) {
            this.previewEmailTarget.style.display = this.emailCheckboxTarget.checked ? 'block' : 'none'
            
            // Update label if available
            if (this.hasEmailLabelTarget && this.previewEmailTarget.querySelector('label')) {
                this.previewEmailTarget.querySelector('label').textContent = this.emailLabelTarget.value
            }
        }
        
        // Phone visibility and label
        if (this.hasPhoneCheckboxTarget && this.hasPreviewPhoneTarget) {
            this.previewPhoneTarget.style.display = this.phoneCheckboxTarget.checked ? 'block' : 'none'
            
            // Update label if available
            if (this.hasPhoneLabelTarget && this.previewPhoneTarget.querySelector('label')) {
                this.previewPhoneTarget.querySelector('label').textContent = this.phoneLabelTarget.value
            }
        }
        
        // Identifier visibility and label
        if (this.hasIdentifierCheckboxTarget && this.hasPreviewIdentifierTarget) {
            this.previewIdentifierTarget.style.display = this.identifierCheckboxTarget.checked ? 'block' : 'none'
            
            // Update label if available
            if (this.hasIdentifierPlaceholderTarget && this.hasPreviewIdentifierLabelTarget) {
                this.previewIdentifierLabelTarget.textContent = this.identifierPlaceholderTarget.value
            }
        }
    }

    // Toggle field card body visibility
    toggleFieldCard(event) {
        const checkbox = event.target
        const fieldType = checkbox.dataset.field
        const showField = checkbox.checked
        
        // Handle field body visibility
        switch (fieldType) {
            case 'first-name':
                if (this.hasFirstNameFieldsTarget) {
                    this.firstNameFieldsTarget.style.display = showField ? 'block' : 'none'
                }
                if (this.hasPreviewFirstNameTarget) {
                    this.previewFirstNameTarget.style.display = showField ? 'block' : 'none'
                }
                break
                
            case 'last-name':
                if (this.hasLastNameFieldsTarget) {
                    this.lastNameFieldsTarget.style.display = showField ? 'block' : 'none'
                }
                if (this.hasPreviewLastNameTarget) {
                    this.previewLastNameTarget.style.display = showField ? 'block' : 'none'
                }
                break
                
            case 'email':
                if (this.hasEmailFieldsTarget) {
                    this.emailFieldsTarget.style.display = showField ? 'block' : 'none'
                }
                if (this.hasPreviewEmailTarget) {
                    this.previewEmailTarget.style.display = showField ? 'block' : 'none'
                }
                break
                
            case 'phone':
                if (this.hasPhoneFieldsTarget) {
                    this.phoneFieldsTarget.style.display = showField ? 'block' : 'none'
                }
                if (this.hasPreviewPhoneTarget) {
                    this.previewPhoneTarget.style.display = showField ? 'block' : 'none'
                }
                break
                
            case 'unique-id':
                if (this.hasIdentifierFieldsTarget) {
                    this.identifierFieldsTarget.style.display = showField ? 'block' : 'none'
                }
                if (this.hasPreviewIdentifierTarget) {
                    this.previewIdentifierTarget.style.display = showField ? 'block' : 'none'
                }
                break
        }
    }

    // Update code preview whenever a form field changes
    updateCodePreview() {
        if (!this.hasCodeContentTarget) return

        // Get all form values
        const configurableValues = this.getFormValues()
        
        // Generate code based on the integration type
        let codeTemplate = ''
        
        if (configurableValues.integrationType === 'cdn') {
            codeTemplate = this.generateCdnCodeTemplate(configurableValues)
        } else {
            codeTemplate = this.generateNpmCodeTemplate(configurableValues)
        }

        // Set the code content
        this.codeContentTarget.textContent = codeTemplate
        
        // Automatically update the live preview when configuration changes
        if (this.hasPreviewIframeTarget) {
            this.renderLivePreview()
        }
    }

    // Get all form values for code generation
    getFormValues() {
        return {
            apiKey: this.hasApiKeyTarget ? this.apiKeyTarget.value : "your_PUBLIC_api_key",
            parentId: this.hasParentIdTarget ? this.parentIdTarget.value : "bynn-verify-form",
            kycLevel: this.hasKycLevelTarget ? this.kycLevelTarget.value : "YK76M6RKT",
            i18n: this.hasI18nTarget ? this.i18nTarget.value : "en",
            integrationType: this.hasIntegrationTypeTarget ? this.integrationTypeTarget.value : "cdn",
            verificationFlow: this.hasVerificationFlowTarget ? this.verificationFlowTarget.value : "embedded",
            
            // First name field
            showFirstName: this.hasFirstNameCheckboxTarget ? this.firstNameCheckboxTarget.checked : true,
            firstNameLabel: this.hasFirstNameLabelTarget ? this.firstNameLabelTarget.value : "First Name",
            firstNameVisible: this.hasFirstNameVisibleCheckboxTarget ? this.firstNameVisibleCheckboxTarget.checked : true,
            firstNameValue: this.hasFirstNameValueTarget ? this.firstNameValueTarget.value : "",
            
            // Last name field
            showLastName: this.hasLastNameCheckboxTarget ? this.lastNameCheckboxTarget.checked : true,
            lastNameLabel: this.hasLastNameLabelTarget ? this.lastNameLabelTarget.value : "Last Name",
            lastNameVisible: this.hasLastNameVisibleCheckboxTarget ? this.lastNameVisibleCheckboxTarget.checked : true,
            lastNameValue: this.hasLastNameValueTarget ? this.lastNameValueTarget.value : "",
            
            // Email field
            showEmail: this.hasEmailCheckboxTarget ? this.emailCheckboxTarget.checked : false,
            emailLabel: this.hasEmailLabelTarget ? this.emailLabelTarget.value : "Email Address",
            emailVisible: this.hasEmailVisibleCheckboxTarget ? this.emailVisibleCheckboxTarget.checked : true,
            emailValue: this.hasEmailValueTarget ? this.emailValueTarget.value : "",
            
            // Phone field
            showPhone: this.hasPhoneCheckboxTarget ? this.phoneCheckboxTarget.checked : false,
            phoneLabel: this.hasPhoneLabelTarget ? this.phoneLabelTarget.value : "Phone Number",
            phoneVisible: this.hasPhoneVisibleCheckboxTarget ? this.phoneVisibleCheckboxTarget.checked : true,
            phoneValue: this.hasPhoneValueTarget ? this.phoneValueTarget.value : "",
            
            // Unique identifier field
            showIdentifier: this.hasIdentifierCheckboxTarget ? this.identifierCheckboxTarget.checked : true,
            identifierLabel: this.hasIdentifierPlaceholderTarget ? this.identifierPlaceholderTarget.value : "Order Number",
            identifierVisible: this.hasIdentifierVisibleCheckboxTarget ? this.identifierVisibleCheckboxTarget.checked : false,
            identifierValue: this.hasIdentifierValueTarget ? this.identifierValueTarget.value : ""
        }
    }

    // Generate CDN integration code
    generateCdnCodeTemplate(params, forPreview = false) {
        const fields = this.generateFieldsArray(params)
        const fieldCode = JSON.stringify(fields, null, 2).replace(/"/g, "'")

        // Always use window properties for preview to avoid variable redeclaration issues
        const variableName = forPreview ? 'window.bynnInstance' : 'const bynn'
        
        // Start building the config object
        let configOptions = 
'    apiKey: \'' + params.apiKey + '\',\n' +
'    parentId: \'' + params.parentId + '\',\n' +
'    kycLevel: \'' + params.kycLevel + '\',\n' +
'    i18n: \'' + params.i18n + '\',\n';

        // Add host in development environment only
        if (typeof window !== 'undefined' && window.isDevelopment === true && window.API_URL) {
            configOptions += '    host: \'' + window.API_URL + '\',\n';
        }
        
        // Add fields
        configOptions += '    fields: ' + fieldCode + ',\n';
        
        // Add event handlers
        configOptions += '    onSession: (error, response, sessionId) => {\n' +
'        if (error) {\n' +
'            console.error(\'Verification error:\', error);\n' +
'        }\n' +
'        if (response) {\n' +
'            console.log(\'Verification onSession id:\', response.sessionId);\n' +
'        }\n' +
'    },\n' +
'    onTimeout: (sessionId) => {\n' +
'        console.log(\'Verification timed out, session id:\', sessionId);\n' +
'    },\n' +
'    onCancel: () => {\n' +
'        console.log(\'Verification canceled\');\n' +
'    },\n' +
'    onComplete: (sessionId) => {\n' +
'        console.log(\'Verification completed, session id:\', sessionId);\n' +
'    },\n' +
'    onError: (sessionId, error) => {\n' +
'        console.log(\'Verification error, session id, error:\', sessionId, error);\n' +
'    },\n' +
'    onStart: (sessionId) => {\n' +
'        console.log(\'Verification started, session id:\', sessionId);\n' +
'    }\n';

        // For iframe preview, use a script that avoids redeclaration issues
        if (forPreview) {
            return '<!-- Bynn Root Element -->\n' +
'<div id="' + params.parentId + '"></div>\n' +
'\n' +
'<!-- Bynn SDK Script -->\n' +
'<script src="' + (typeof window !== 'undefined' && window.BYNN_SDK_URL ? window.BYNN_SDK_URL : "https://static.bynn.com/sdk/js/1.2.1/bynn.min.js") + '"></script>\n' +
'\n' +
'<!-- Bynn Configuration -->\n' +
'<script>\n' +
'  // Use a function to avoid variable redeclaration issues\n' +
'  (function initBynn() {\n' +
'    window.bynnInstance = window.createBynn.default({\n' +
configOptions +
'    });\n' +
'    window.bynnInstance.mount({\n' +
'        submitBtnText: \'Start Verification\',\n' +
'        loadingText: \'Starting verification...\'\n' +
'    });\n' +
'  })();\n' +
'</script>';
        } else {
            // Regular code for display
            return '<!-- Bynn Root Element -->\n' +
'<div id="' + params.parentId + '"></div>\n' +
'\n' +
'<!-- Bynn SDK Script -->\n' +
'<script src="https://static.bynn.com/sdk/js/1.2.11/bynn.min.js"></script>\n' +
'\n' +
'<!-- Bynn Configuration -->\n' +
'<script>\n' +
'    const bynn = window.createBynn.default({\n' +
'        apiKey: \'' + params.apiKey + '\',\n' +
(typeof window !== 'undefined' && window.isDevelopment === true ? '        host: \'https://rails.bynn.io/api\',\n' : '') +
'        parentId: \'' + params.parentId + '\',\n' +
'        i18n: \'' + params.i18n + '\',\n' +
'        kycLevel: \'' + params.kycLevel + '\',\n' +
'        fields: ' + fieldCode + ',\n' +
'        onSession: (error, response, sessionId) => {\n' +
'            if (error) {\n' +
'                console.error(\'Verification error:\', error);\n' +
'            }\n' +
'            if (response) {\n' +
'                console.log(\'Verification onSession id:\', response.sessionId);\n' +
'            }\n' +
'        },\n' +
'        onTimeout: (sessionId) => {\n' +
'            console.log(\'Verification timed out, session id:\', sessionId);\n' +
'        },\n' +
'        onCancel: () => {\n' +
'            console.log(\'Verification canceled\');\n' +
'        },\n' +
'        onComplete: (sessionId) => {\n' +
'            console.log(\'Verification completed, session id:\', sessionId);\n' +
'        },\n' +
'        onError: (sessionId, error) => {\n' +
'            console.log(\'Verification error, session id, error:\', sessionId, error);\n' +
'        },\n' +
'        onStart: (sessionId) => {\n' +
'            console.log(\'Verification started, session id:\', sessionId);\n' +
'        }\n' +
'    });\n' +
'\n' +
'    bynn.mount({\n' +
'        submitBtnText: \'Start Verification\',\n' +
'        loadingText: \'Starting verification...\'\n' +
'    });\n' +
'</script>';
        }
    }

    // Generate NPM integration code
    generateNpmCodeTemplate(params) {
        const fields = this.generateFieldsArray(params)
        const fieldCode = JSON.stringify(fields, null, 2).replace(/"/g, "'")
        
        // Start building the config object
        let configOptions = 
'  apiKey: \'' + params.apiKey + '\',\n' +
'  parentId: \'' + params.parentId + '\',\n' +
'  kycLevel: \'' + params.kycLevel + '\',\n' +
'  i18n: \'' + params.i18n + '\',\n';

        // Add host in development environment
        if (typeof window !== 'undefined' && window.isDevelopment === true && window.API_URL) {
            configOptions += '  host: \'' + window.API_URL + '\',\n';
        }
        
        // Add fields
        configOptions += '  fields: ' + fieldCode + ',\n';
        
        // Add event handlers
        configOptions += '  onSession: (error, response, sessionId) => {\n' +
'    if (error) {\n' +
'      console.error(\'Verification error:\', error);\n' +
'    }\n' +
'    if (response) {\n' +
'      console.log(\'Verification onSession id:\', response.sessionId);\n' +
'    }\n' +
'  },\n' +
'  onTimeout: (sessionId) => {\n' +
'    console.log(\'Verification timed out, session id:\', sessionId);\n' +
'  },\n' +
'  onCancel: () => {\n' +
'    console.log(\'Verification canceled\');\n' +
'  },\n' +
'  onComplete: (sessionId) => {\n' +
'    console.log(\'Verification completed, session id:\', sessionId);\n' +
'  },\n' +
'  onError: (sessionId, error) => {\n' +
'    console.log(\'Verification error, session id, error:\', sessionId, error);\n' +
'  },\n' +
'  onStart: (sessionId) => {\n' +
'    console.log(\'Verification started, session id:\', sessionId);\n' +
'  }\n';

        return '// Install the SDK: npm install @bynn-intelligence/websdk\n' +
'\n' +
'// Import the SDK in your JavaScript/TypeScript file\n' +
'import { createBynn } from \'@bynn-intelligence/websdk\';\n' +
'\n' +
'// In your component/page:\n' +
'\n' +
'// 1. Create a container element in your HTML\n' +
'// <div id="' + params.parentId + '"></div>\n' +
'\n' +
'// 2. Initialize and mount the SDK\n' +
'const bynn = createBynn({\n' +
configOptions +
'});\n' +
'\n' +
'// 3. Mount the verification form\n' +
'bynn.mount({\n' +
'  submitBtnText: \'Start Verification\',\n' +
'  loadingText: \'Starting verification...\'\n' +
'});'
    }

    // Generate fields array for SDK configuration
    generateFieldsArray(params) {
        const fields = []
        
        // Add first name field if enabled
        if (params.showFirstName) {
            const firstNameField = {
                name: 'first_name',
                visible: params.firstNameVisible
            }
            
            // Add custom label if provided
            if (params.firstNameLabel) {
                firstNameField.label = params.firstNameLabel
            }
            
            // Add default value if provided
            if (params.firstNameValue) {
                firstNameField.value = params.firstNameValue
            }
            
            fields.push(firstNameField)
        }
        
        // Add last name field if enabled
        if (params.showLastName) {
            const lastNameField = {
                name: 'last_name',
                visible: params.lastNameVisible
            }
            
            // Add custom label if provided
            if (params.lastNameLabel) {
                lastNameField.label = params.lastNameLabel
            }
            
            // Add default value if provided
            if (params.lastNameValue) {
                lastNameField.value = params.lastNameValue
            }
            
            fields.push(lastNameField)
        }
        
        // Add email field if enabled
        if (params.showEmail) {
            const emailField = {
                name: 'email_address',
                visible: params.emailVisible
            }
            
            // Add custom label if provided
            if (params.emailLabel) {
                emailField.label = params.emailLabel
            }
            
            // Add default value if provided
            if (params.emailValue) {
                emailField.value = params.emailValue
            }
            
            fields.push(emailField)
        }
        
        // Add phone field if enabled
        if (params.showPhone) {
            const phoneField = {
                name: 'phone_number',
                visible: params.phoneVisible
            }
            
            // Add custom label if provided
            if (params.phoneLabel) {
                phoneField.label = params.phoneLabel
            }
            
            // Add default value if provided
            if (params.phoneValue) {
                phoneField.value = params.phoneValue
            }
            
            fields.push(phoneField)
        }
        
        // Add identifier field if enabled
        if (params.showIdentifier) {
            const identifierField = {
                name: 'unique_id',
                visible: params.identifierVisible
            }
            
            // Add custom label if provided
            if (params.identifierLabel) {
                identifierField.label = params.identifierLabel
            }
            
            // Add default value if provided
            if (params.identifierValue) {
                identifierField.value = params.identifierValue
            }
            
            fields.push(identifierField)
        }
        
        return fields
    }

    // Copy code to clipboard
    copyToClipboard(event) {
        if (!this.hasCodeContentTarget) return
        
        // Get the generated code
        const codeText = this.codeContentTarget.textContent
        
        // Copy to clipboard
        navigator.clipboard.writeText(codeText)
            .then(() => {
                // Update button icon to show success
                const icon = this.copyButtonTarget.querySelector('i')
                const originalClass = icon.className
                icon.className = 'fas fa-check'
                
                // Reset icon after 2 seconds
                setTimeout(() => {
                    icon.className = originalClass
                }, 2000)
            })
            .catch(err => {
                console.error('Failed to copy code: ', err)
            })
    }
    
    // Copy JavaScript code from preview window
    copyJsCodeFromPreview(event) {
        if (!this.hasCodeContentTarget) return
        
        // Get the generated code
        const codeText = this.codeContentTarget.textContent
        
        // Copy to clipboard
        navigator.clipboard.writeText(codeText)
            .then(() => {
                // Update button to show success
                const button = this.previewCopyBtnTarget
                const originalText = button.textContent
                const originalClass = button.className
                
                button.textContent = 'Copied!'
                button.classList.remove('btn-outline-primary')
                button.classList.add('btn-success')
                
                // Reset button after 2 seconds
                setTimeout(() => {
                    button.textContent = originalText
                    button.className = originalClass
                }, 2000)
            })
            .catch(err => {
                console.error('Failed to copy code: ', err)
            })
    }

    // Render a live preview inside a sandbox iframe
    renderLivePreview() {
        if (!this.hasPreviewIframeTarget) return;
        
        // Get configuration values
        const config = this.getFormValues();
        
        // Generate the CDN code to insert in the iframe
        const cdnCode = this.generateCdnCodeTemplate(config, true);
        
        // Get the iframe
        const iframe = this.previewIframeTarget;
        
        // Create completely isolated content document
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        
        // Create a fully formed HTML document with the integration code
        // Add a <base> tag to ensure all resources load correctly
        // Add CSS to make the iframe content match the parent page's styling
        iframeDoc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <base href="${window.location.origin}">
                <title>Bynn SDK Preview</title>
                <style>
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                        padding: 20px;
                        margin: 0;
                    }
                    
                    /* Fix for modal windows to appear properly within iframe */
                    .bynn-modal-overlay {
                        position: fixed !important;
                        z-index: 9999 !important;
                    }
                </style>
            </head>
            <body>
                <!-- Add the Rails environment and SDK URL -->
                <script>
                    window.isDevelopment = ${typeof window !== 'undefined' && window.isDevelopment === true};
                    window.BYNN_SDK_URL = "${typeof window !== 'undefined' && window.BYNN_SDK_URL ? window.BYNN_SDK_URL : ''}";
                    window.API_URL = "${typeof window !== 'undefined' && window.API_URL ? window.API_URL : ''}";
                </script>
                
                <!-- Insert the generated integration code -->
                ${cdnCode}
                
                <!-- Add resize observer to adjust iframe height -->
                <script>
                    // Resize the iframe to match content height - use IIFE to avoid variable redeclaration
                    (function setupResizeObserver() {
                        if (window._bynnResizeObserver) {
                            window._bynnResizeObserver.disconnect();
                        }
                        
                        window._bynnResizeObserver = new ResizeObserver(entries => {
                            // Get the height of the document
                            const height = document.body.scrollHeight;
                            
                            // Send message to parent window
                            window.parent.postMessage({ type: 'resize-iframe', height: height }, '*');
                        });
                        
                        // Observe the body element
                        window._bynnResizeObserver.observe(document.body);
                    })();
                </script>
            </body>
            </html>
        `);
        iframeDoc.close();
        
        // Add event listener for iframe messages
        window.addEventListener('message', this.handleIframeMessage.bind(this));
        
        // If we're in fullscreen mode, also update the fullscreen iframe
        if (this.hasFullscreenContainerTarget && 
            !this.fullscreenContainerTarget.classList.contains('d-none')) {
            this.copyPreviewToFullscreen();
        }
    }
    
    // Handle messages from the iframe
    handleIframeMessage(event) {
        // Check if it's a resize message
        if (event.data && event.data.type === 'resize-iframe') {
            const iframe = this.previewIframeTarget;
            if (iframe) {
                // Add a bit of padding to avoid scrolling
                iframe.style.height = (event.data.height + 30) + 'px';
            }
        }
    }
    
    // Toggle between normal preview and fullscreen mode
    togglePreviewSize() {
        if (!this.hasPreviewIframeTarget || !this.hasFullscreenContainerTarget || !this.hasFullscreenIframeTarget) return;
        
        // Check if we're already in fullscreen mode
        const isFullscreen = !this.fullscreenContainerTarget.classList.contains('d-none');
        
        if (isFullscreen) {
            // Exit fullscreen mode
            this.fullscreenContainerTarget.classList.add('d-none');
            
            // Change the main toggle button icon back to expand
            if (this.hasToggleSizeIconTarget) {
                this.toggleSizeIconTarget.classList.remove('fa-compress-alt');
                this.toggleSizeIconTarget.classList.add('fa-expand-alt');
            }
            
            // Re-enable scrolling on the body
            document.body.style.overflow = '';
        } else {
            // Enter fullscreen mode
            
            // First, ensure the fullscreen iframe has the same content as the preview iframe
            this.copyPreviewToFullscreen();
            
            // Show the fullscreen container
            this.fullscreenContainerTarget.classList.remove('d-none');
            
            // Change the main toggle button icon to compress
            if (this.hasToggleSizeIconTarget) {
                this.toggleSizeIconTarget.classList.remove('fa-expand-alt');
                this.toggleSizeIconTarget.classList.add('fa-compress-alt');
            }
            
            // Set appropriate background color based on theme
            const isDarkMode = document.querySelector('html').getAttribute('data-bs-theme') === 'dark';
            this.fullscreenContainerTarget.style.backgroundColor = isDarkMode ? '#1a1a1a' : '#ffffff';
            
            // Prevent scrolling on the body while in fullscreen
            document.body.style.overflow = 'hidden';
        }
    }
    
    // Copy the content from the preview iframe to the fullscreen iframe
    copyPreviewToFullscreen() {
        if (!this.hasPreviewIframeTarget || !this.hasFullscreenIframeTarget) return;
        
        // Get the current configuration and render it in the fullscreen iframe
        const config = this.getFormValues();
        const cdnCode = this.generateCdnCodeTemplate(config, true);
        
        // Get the fullscreen iframe
        const fullscreenIframe = this.fullscreenIframeTarget;
        const iframeDoc = fullscreenIframe.contentDocument || fullscreenIframe.contentWindow.document;
        
        // Write the content to the fullscreen iframe
        iframeDoc.open();
        iframeDoc.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <base href="${window.location.origin}">
                <title>Bynn SDK Preview</title>
                <style>
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
                        padding: 20px;
                        margin: 0;
                    }
                </style>
            </head>
            <body>
                <!-- Add the Rails environment and SDK URL -->
                <script>
                    window.isDevelopment = ${typeof window !== 'undefined' && window.isDevelopment === true};
                    window.BYNN_SDK_URL = "${typeof window !== 'undefined' && window.BYNN_SDK_URL ? window.BYNN_SDK_URL : ''}";
                    window.API_URL = "${typeof window !== 'undefined' && window.API_URL ? window.API_URL : ''}";
                </script>
                
                <!-- Insert the generated integration code -->
                ${cdnCode}
            </body>
            </html>
        `);
        iframeDoc.close();
    }
    
    // Clean up when controller is disconnected
    disconnect() {
        // Remove event listeners
        window.removeEventListener('message', this.handleIframeMessage.bind(this));
        
        // Disconnect the theme observer
        if (this.themeObserver) {
            this.themeObserver.disconnect();
        }
        
        // Remove any style elements we added to document.head
        if (this.styleElement && document.head.contains(this.styleElement)) {
            document.head.removeChild(this.styleElement);
        }
        
        // Clean up any global variables we created
        if (window.bynnInstancePreview) {
            delete window.bynnInstancePreview;
        }
        
        // Clean up any modal elements that might be left in the DOM
        const modalOverlay = document.querySelector('.bynn-modal-overlay');
        if (modalOverlay) {
            modalOverlay.remove();
        }
        
        // Clean up the iframe if it exists
        if (this.hasPreviewIframeTarget && this.previewIframeTarget) {
            try {
                const iframe = this.previewIframeTarget;
                if (iframe.contentWindow && iframe.contentDocument) {
                    iframe.contentDocument.open();
                    iframe.contentDocument.write('');
                    iframe.contentDocument.close();
                }
            } catch (error) {
                console.warn('Error cleaning up iframe:', error);
            }
        }
        
        // Clean up the hosted preview iframe if it exists
        if (this.hasPreviewFrameTarget && this.previewFrameTarget) {
            try {
                const iframe = this.previewFrameTarget;
                
                // Disconnect any resize observers we created
                if (iframe._resizeObserver) {
                    iframe._resizeObserver.disconnect();
                    iframe._resizeObserver = null;
                }
                
                // Clean up iframe content
                if (iframe.contentWindow && iframe.contentDocument) {
                    iframe.contentDocument.open();
                    iframe.contentDocument.write('');
                    iframe.contentDocument.close();
                }
            } catch (error) {
                console.warn('Error cleaning up hosted preview iframe:', error);
            }
        }
        
        // Re-enable scrolling on the body if we were in maximized mode
        document.body.style.overflow = '';
    }
}